import type { PackScanStatus, PackSubmissionStatus } from '@planuze/admin-contracts';
import type { StatusBadge } from '@planuze/ui';
import type { ComponentProps } from 'react';
import type { SubmissionAction } from '../../hooks/use-pack-submissions.js';

type Tone = NonNullable<ComponentProps<typeof StatusBadge>['variant']>;

/* `switch` em vez de Record — as chaves do enum incluem `changes_requested`
   (snake_case do backend) e useNamingConvention rejeita propriedade snake_case;
   case com string-literal não é flagueado. */
export function submissionTone(status: PackSubmissionStatus): Tone {
  switch (status) {
    case 'submitted':
    case 'scanning':
      return 'info';
    case 'reviewing':
    case 'changes_requested':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'destructive';
    case 'cancelled':
      return 'neutral';
  }
}

export interface SubmissionStatusLabels {
  readonly submitted: string;
  readonly scanning: string;
  readonly reviewing: string;
  readonly approved: string;
  readonly rejected: string;
  readonly changesRequested: string;
  readonly cancelled: string;
}

/* Default pt-BR — mantém os call sites que não passam `labels` (testes, uso fora do provedor de
   idioma) funcionando sem quebrar. Componentes bilíngues passam os rótulos resolvidos do próprio
   `usePackSubmissionsContent()` (ADR-0811). */
const DEFAULT_SUBMISSION_STATUS_LABELS: SubmissionStatusLabels = {
  submitted: 'Enviada',
  scanning: 'Escaneando',
  reviewing: 'Em revisão',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
  changesRequested: 'Mudanças pedidas',
  cancelled: 'Cancelada',
};

export function submissionLabel(
  status: PackSubmissionStatus,
  labels: SubmissionStatusLabels = DEFAULT_SUBMISSION_STATUS_LABELS,
): string {
  switch (status) {
    case 'submitted':
      return labels.submitted;
    case 'scanning':
      return labels.scanning;
    case 'reviewing':
      return labels.reviewing;
    case 'approved':
      return labels.approved;
    case 'rejected':
      return labels.rejected;
    case 'changes_requested':
      return labels.changesRequested;
    case 'cancelled':
      return labels.cancelled;
  }
}

/* Status do scan do scanner (ADR-0370): `pass` limpo, `warn` com alertas, `fail` bloqueia,
   `pending` ainda não rodou. Tom/rótulo do badge da Visão geral da submissão. */
export function scanStatusTone(status: PackScanStatus): Tone {
  switch (status) {
    case 'pass':
      return 'success';
    case 'warn':
      return 'warning';
    case 'fail':
      return 'destructive';
    case 'pending':
      return 'neutral';
  }
}

export interface ScanStatusLabels {
  readonly pass: string;
  readonly warn: string;
  readonly fail: string;
  readonly pending: string;
}

const DEFAULT_SCAN_STATUS_LABELS: ScanStatusLabels = {
  pass: 'Aprovado',
  warn: 'Com alertas',
  fail: 'Reprovado',
  pending: 'Pendente',
};

export function scanStatusLabel(
  status: PackScanStatus,
  labels: ScanStatusLabels = DEFAULT_SCAN_STATUS_LABELS,
): string {
  switch (status) {
    case 'pass':
      return labels.pass;
    case 'warn':
      return labels.warn;
    case 'fail':
      return labels.fail;
    case 'pending':
      return labels.pending;
  }
}

/* Ações válidas POR estado, espelhando a state-machine do admin-service (ADR-0448) —
   o botão só dispara transição válida, nunca um 409 `invalid_transition`:
   - `submitted`/`scanning` → "Iniciar revisão" (assume p/ review manual).
   - `reviewing` → aprovar / pedir mudanças / rejeitar.
   - `changes_requested` (aguarda reenvio) e terminais → nenhuma ação do admin. */
export function availableActions(status: PackSubmissionStatus): readonly SubmissionAction[] {
  switch (status) {
    case 'submitted':
    case 'scanning':
      return ['start-review'];
    case 'reviewing':
      return ['approve', 'request-changes', 'reject'];
    /* Rejeitada → "Reabrir" (rejected → reviewing, ADR-0459): retoma a MESMA submissão. */
    case 'rejected':
      return ['reopen'];
    default:
      return [];
  }
}
